import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  HStack,
  Grid,
  Heading,
  Text,
  Button,
} from "@chakra-ui/react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import CTAButton from "../components/common/CTAButton";
import { disciplines } from "../data/disciplines";
import { FaArrowLeft, FaCheckCircle, FaLightbulb, FaBriefcase } from "react-icons/fa";

export default function DisciplineDetail() {
  const { disciplineId } = useParams();
  const navigate = useNavigate();

  // Find the discipline
  const discipline = disciplines.find((d) => d.id === disciplineId);

  if (!discipline) {
    return (
      <Box>
        <Navbar />
        <Container maxW="container.xl" py={20}>
          <VStack spacing={4} textAlign="center">
            <Heading size="lg" color="#1E293B">
              Discipline not found
            </Heading>
            <Text color="#475569">The discipline you're looking for doesn't exist.</Text>
            <Button
              onClick={() => navigate("/disciplines")}
              bg="#10B981"
              color="white"
              _hover={{ bg: "#0D9467" }}
            >
              Back to Disciplines
            </Button>
          </VStack>
        </Container>
        <Footer />
      </Box>
    );
  }

  const Icon = discipline.icon;

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title={discipline.name}
        subtitle={discipline.tagline}
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <Button
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate("/disciplines")}
            bg="white"
            color="#10B981"
            _hover={{ bg: "#F8FAFC" }}
            size="lg"
          >
            Back to Disciplines
          </Button>
          <CTAButton to="/for-students" size="lg">
            Start Learning
          </CTAButton>
        </HStack>
      </Hero>

      {/* Industry Context */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="left" w="full">
              <Box display="flex" alignItems="center" gap={3} mb={4}>
                {Icon && <Box fontSize="3xl" color="#10B981"><Icon /></Box>}
                <Heading as="h2" size="xl" color="#1E293B">
                  Why This Discipline?
                </Heading>
              </Box>
              <Text fontSize="lg" color="#475569" lineHeight={1.8}>
                {discipline.industryContext}
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  {discipline.skills.length}+ Skills
                </Heading>
                <Text color="#475569">
                  Master industry-relevant skills that employers actually need.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  {discipline.projects.length} Capstone Projects
                </Heading>
                <Text color="#475569">
                  Build real projects that showcase your capabilities.
                </Text>
              </Box>

              <Box p={6} bg="white" borderRadius="lg" border="1px solid #E2E8F0">
                <Heading size="sm" color="#10B981" mb={3}>
                  {discipline.careerRoles.length}+ Career Paths
                </Heading>
                <Text color="#475569">
                  Multiple high-paying job roles you can move into.
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Skills Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Skills You'll Master
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                A comprehensive curriculum designed to make you job-ready.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              {discipline.skills.map((skill, idx) => (
                <HStack key={idx} spacing={4} p={4} bg="#F8FAFC" borderRadius="lg">
                  <Box color="#10B981" fontSize="xl">
                    <FaCheckCircle />
                  </Box>
                  <Text color="#1E293B" fontWeight="500">
                    {skill}
                  </Text>
                </HStack>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Tools & Technologies */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Tools & Technologies
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Industry-standard tools and frameworks you'll work with.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
              gap={6}
              w="full"
            >
              {discipline.tools.map((tool, idx) => (
                <Box
                  key={idx}
                  p={4}
                  bg="white"
                  borderRadius="lg"
                  border="1px solid #E2E8F0"
                  textAlign="center"
                  _hover={{ borderColor: "#10B981", boxShadow: "0 4px 12px rgba(16, 185, 129, 0.1)" }}
                  transition="all 0.3s"
                >
                  <Text fontWeight="600" color="#1E293B">
                    {tool}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Capstone Projects */}
      {false &&
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Capstone Projects
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Build production-grade projects that form your portfolio and impress employers.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={8} w="full">
              {discipline.projects.map((project, idx) => (
                <Box key={idx} p={6} bg="#F8FAFC" borderRadius="lg" border="1px solid #E2E8F0">
                  <HStack mb={3}>
                    <Box color="#10B981" fontSize="xl">
                      <FaLightbulb />
                    </Box>
                    <Heading size="sm" color="#1E293B">
                      Project {idx + 1}
                    </Heading>
                  </HStack>
                  <Text color="#475569" mb={4}>
                    {project}
                  </Text>
                  <Text fontSize="sm" color="#64748B">
                    You'll build this from scratch, learning best practices and industry standards.
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>
}

      {/* Career Roles */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Career Roles You Can Pursue
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                Multiple high-paying job roles across different companies and industries.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={8} w="full">
              {discipline.careerRoles.map((role, idx) => (
                <Box
                  key={idx}
                  p={6}
                  bg="white"
                  borderRadius="lg"
                  border="1px solid #E2E8F0"
                  textAlign="center"
                  _hover={{ bg: "#ECFDF5", borderColor: "#10B981" }}
                  transition="all 0.3s"
                >
                  <HStack justify="center" mb={3}>
                    <Box color="#10B981" fontSize="2xl">
                      <FaBriefcase />
                    </Box>
                  </HStack>
                  <Text fontWeight="600" color="#1E293B">
                    {role}
                  </Text>
                </Box>
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Related Disciplines */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#1E293B">
                Explore Other Disciplines
              </Heading>
              <Text fontSize="lg" color="#475569" maxW="2xl">
                You can also learn multiple disciplines to expand your skill set.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {disciplines
                .filter((d) => d.id !== disciplineId)
                .slice(0, 3)
                .map((disc) => {
                  const DisciplineIcon = disc.icon;
                  return (
                    <Box
                      key={disc.id}
                      p={6}
                      bg="white"
                      borderRadius="lg"
                      border="1px solid #E2E8F0"
                      _hover={{ boxShadow: "0 12px 40px rgba(16, 185, 129, 0.2)", transform: "translateY(-4px)" }}
                      transition="all 0.3s"
                      cursor="pointer"
                      onClick={() => navigate(`/disciplines/${disc.id}`)}
                    >
                      <Box fontSize="2xl" color="#10B981" mb={3}>
                        <DisciplineIcon />
                      </Box>
                      <Heading size="sm" color="#1E293B" mb={2}>
                        {disc.name}
                      </Heading>
                      <Text fontSize="sm" color="#475569">
                        {disc.description}
                      </Text>
                    </Box>
                  );
                })}
            </Grid>

            <CTAButton to="/disciplines" size="lg">
              View All Disciplines
            </CTAButton>
          </VStack>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Ready to Master {discipline.name}?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              Start your journey to a high-paying tech career. Learn job-ready skills, build projects, and land your dream role.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton to="/for-students" size="lg">
                Start Learning
              </CTAButton>
              <CTAButton to="/contact" variant="outline" size="lg">
                Talk to an Advisor
              </CTAButton>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
